import { useState, useEffect } from 'react';

const LP = {
  midnight: '#1C2033',
  iconPurple: '#5F3DC4',
  offWhite: '#F4F3F8',
  mist: '#E8E6F0',
  mutedSlate: '#6B6F85',
  gradient: 'linear-gradient(135deg,#5F3DC4,#C4A8F0)',
};

const FONT_DISPLAY = "'BW Modelica','Poppins','Helvetica Neue',sans-serif";
const FONT_BODY = "'DM Sans',sans-serif";

const FAQS: Record<string, Array<{ q: string; a: string }>> = {
  buyers: [
    { q: 'How does bidding work?',
      a: 'Sellers list items live during a show. Tap to bid, and you\'ll see your bid appear in real time. If you\'re the highest bidder when the timer hits zero, the item is yours and we\'ll charge the card on your account.' },
    { q: 'When will my order ship?',
      a: 'Sellers print labels right after the show wraps. Most orders ship within 2\u20133 business days. You\'ll get an email with tracking as soon as the label is scanned by the carrier.' },
    { q: 'Can I cancel a winning bid?',
      a: 'Bids are binding, so we can\'t cancel them on request. If something went wrong (you bid by mistake, or the item arrived not as described), reply to your order confirmation email and we\'ll work it out.' },
    { q: 'What\u2019s your return policy?',
      a: 'Items are sold as described by the seller. If your item arrives damaged or significantly different from the show, email us within 7 days of delivery and we\'ll help with a return or refund.' },
    { q: 'How do I update my card or address?',
      a: 'Open the app, tap your profile, then Account \u2192 Payment methods or Shipping addresses. Changes take effect on your next order.' },
  ],
  sellers: [
    { q: 'How do I apply to sell on Snagg?',
      a: 'We\'re onboarding sellers in cohorts. Apply from the For sellers page \u2014 we review applications weekly and reach out by email.' },
    { q: 'When do I get paid?',
      a: 'Payouts run through Stripe Connect, 3 business days after the buyer confirms delivery or after the auto-confirm window. You can see upcoming and past payouts in Seller Hub \u2192 Payouts.' },
    { q: 'How do shipping labels work?',
      a: 'After your show, Seller Hub generates a prepaid label for each order at your configured rate. Print, pack, drop off \u2014 tracking syncs back automatically.' },
    { q: 'Can I edit a listing during a live show?',
      a: 'You can edit title, description, and starting price up until the item goes up for bidding. Once bidding opens, the listing is locked.' },
    { q: 'My stream dropped mid-show \u2014 what happens?',
      a: 'Reconnect as soon as you can. Active bids pause, and buyers see a \u201cReconnecting\u201d banner. If you can\'t recover within 5 minutes the show ends and any completed sales still go through.' },
  ],
};

function MailIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="3"/>
      <path d="m2 7 10 6 10-6"/>
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={LP.iconPurple} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5"/>
    </svg>
  );
}

function ChevIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6"/>
    </svg>
  );
}

function SupportHero() {
  const [copied, setCopied] = useState(false);
  const email = 'support@snagg.live';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* clipboard not available */ }
  };

  return (
    <section style={heroStyles.section}>
      <div style={heroStyles.eyebrow}>SUPPORT</div>
      <h1 style={heroStyles.h1}>How can we help?</h1>
      <p style={heroStyles.p}>
        We're a small team, and right now every support request goes
        straight to a human via email. We usually reply within one business day.
      </p>

      <div style={heroStyles.card}>
        <div style={heroStyles.cardTop}>
          <div style={heroStyles.iconWrap}>
            <MailIcon />
          </div>
          <div style={heroStyles.cardCopy}>
            <div style={heroStyles.cardKicker}>Email us</div>
            <div style={heroStyles.cardLabel}>Best for everything — orders, payouts, bugs, anything else.</div>
          </div>
        </div>

        <div style={heroStyles.emailRow}>
          <a href={`mailto:${email}`} style={heroStyles.emailAddress}>{email}</a>
          <div style={heroStyles.actionsRow}>
            <button onClick={copy} style={heroStyles.copyBtn} aria-label="Copy email address">
              {copied ? <><CheckIcon /> Copied</> : <><CopyIcon /> Copy</>}
            </button>
            <a href={`mailto:${email}`} style={heroStyles.openBtn}>Open mail &rarr;</a>
          </div>
        </div>

        <div style={heroStyles.tip}>
          <div style={heroStyles.tipKicker}>To help us reply faster</div>
          <ul style={heroStyles.tipList}>
            <li>Include your account email and order or show ID if relevant.</li>
            <li>Screenshots are welcome — they save us a back-and-forth.</li>
            <li>For a missing or damaged item, please write in within 7 days of delivery.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

const heroStyles: Record<string, React.CSSProperties> = {
  section: { maxWidth: 760, margin: '0 auto', padding: '72px 24px 32px',
             fontFamily: FONT_BODY, color: LP.midnight, textAlign: 'center' },
  eyebrow: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: 12, color: LP.iconPurple,
             letterSpacing: '.16em', marginBottom: 16 },
  h1: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 64, lineHeight: 1.02,
        letterSpacing: '-0.03em', color: LP.midnight, margin: '0 0 16px' },
  p: { fontFamily: FONT_BODY, fontSize: 18, lineHeight: 1.55, color: LP.mutedSlate,
       maxWidth: 520, margin: '0 auto 40px' },

  card: { background: '#fff', border: `1px solid ${LP.mist}`, borderRadius: 24,
          padding: 32, textAlign: 'left' as const, boxShadow: '0 10px 40px rgba(28,32,51,0.06)' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  iconWrap: { width: 48, height: 48, borderRadius: 14, background: LP.gradient,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardCopy: { display: 'flex', flexDirection: 'column' as const, gap: 2 },
  cardKicker: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22,
                letterSpacing: '-0.01em', color: LP.midnight },
  cardLabel: { fontFamily: FONT_BODY, fontSize: 14, color: LP.mutedSlate, lineHeight: 1.5 },

  emailRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '20px 24px', background: LP.offWhite, borderRadius: 16,
              gap: 16, flexWrap: 'wrap' as const },
  emailAddress: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 24,
                  letterSpacing: '-0.01em', color: LP.midnight, textDecoration: 'none' },
  actionsRow: { display: 'flex', alignItems: 'center', gap: 8 },
  copyBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
             background: '#fff', borderWidth: 1, borderStyle: 'solid', borderColor: LP.mist, borderRadius: 999,
             fontSize: 14, fontWeight: 600, fontFamily: FONT_BODY, color: LP.midnight,
             cursor: 'pointer', minHeight: 40 },
  openBtn: { display: 'inline-flex', alignItems: 'center', padding: '10px 18px',
             background: LP.midnight, color: '#fff', borderRadius: 999,
             fontSize: 14, fontWeight: 600, fontFamily: FONT_BODY, textDecoration: 'none',
             minHeight: 40 },

  tip: { marginTop: 24, padding: '20px 24px', background: 'rgba(95,61,196,0.04)',
         borderWidth: 1, borderStyle: 'solid', borderColor: LP.mist, borderRadius: 16 },
  tipKicker: { fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: LP.iconPurple,
               letterSpacing: '.06em', textTransform: 'uppercase' as const, marginBottom: 8 },
  tipList: { margin: 0, paddingLeft: 18, fontFamily: FONT_BODY, fontSize: 14,
             lineHeight: 1.6, color: LP.midnight },
};

function FAQSection() {
  const [tab, setTab] = useState<'buyers' | 'sellers'>('buyers');
  const [open, setOpen] = useState(0);
  const items = FAQS[tab];

  useEffect(() => { setOpen(0); }, [tab]);

  return (
    <section style={faqStyles.section}>
      <div style={faqStyles.head}>
        <h2 style={faqStyles.h2}>Common questions</h2>
        <div style={faqStyles.tabs} role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'buyers'}
            onClick={() => setTab('buyers')}
            style={{ ...faqStyles.tab, ...(tab === 'buyers' ? faqStyles.tabActive : {}) }}>
            Buyers
          </button>
          <button
            role="tab"
            aria-selected={tab === 'sellers'}
            onClick={() => setTab('sellers')}
            style={{ ...faqStyles.tab, ...(tab === 'sellers' ? faqStyles.tabActive : {}) }}>
            Sellers
          </button>
        </div>
      </div>

      <div style={faqStyles.list}>
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <div key={`${tab}-${i}`} style={{ ...faqStyles.row, ...(isOpen ? faqStyles.rowOpen : {}) }}>
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                style={faqStyles.qBtn}
                aria-expanded={isOpen}>
                <span style={faqStyles.q}>{it.q}</span>
                <span style={{ ...faqStyles.chev, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <ChevIcon />
                </span>
              </button>
              <div style={{ ...faqStyles.aWrap, maxHeight: isOpen ? 400 : 0,
                           opacity: isOpen ? 1 : 0, paddingBottom: isOpen ? 20 : 0 }}>
                <p style={faqStyles.a}>{it.a}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div style={faqStyles.stillStuck}>
        <span style={{ color: LP.mutedSlate }}>Didn't find it?</span>
        <a href="mailto:support@snagg.live" style={faqStyles.stillLink}>
          Email support@snagg.live &rarr;
        </a>
      </div>
    </section>
  );
}

const faqStyles: Record<string, React.CSSProperties> = {
  section: { maxWidth: 760, margin: '0 auto', padding: '48px 24px 96px', fontFamily: FONT_BODY },
  head: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 24, marginBottom: 24, flexWrap: 'wrap' as const },
  h2: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 36, letterSpacing: '-0.02em',
        color: LP.midnight, margin: 0 },
  tabs: { display: 'inline-flex', background: LP.offWhite, padding: 4, borderRadius: 999,
          borderWidth: 1, borderStyle: 'solid', borderColor: LP.mist },
  tab: { padding: '8px 20px', fontFamily: FONT_BODY, fontWeight: 600, fontSize: 14,
         color: LP.mutedSlate, background: 'transparent', border: 'none', borderRadius: 999,
         cursor: 'pointer', transition: 'background-color .2s, color .2s', minHeight: 36 },
  tabActive: { background: '#fff', color: LP.midnight, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' },

  list: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  row: { background: '#fff', borderWidth: 1, borderStyle: 'solid', borderColor: LP.mist, borderRadius: 16,
         transition: 'border-color .2s' },
  rowOpen: { borderColor: 'rgba(95,61,196,0.25)' },
  qBtn: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          width: '100%', padding: '20px 24px', background: 'transparent', border: 'none',
          textAlign: 'left' as const, cursor: 'pointer', fontFamily: FONT_BODY },
  q: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: 16, color: LP.midnight, lineHeight: 1.4 },
  chev: { color: LP.mutedSlate, display: 'inline-flex', transition: 'transform .2s' },
  aWrap: { overflow: 'hidden' as const, transition: 'max-height .25s ease, opacity .2s, padding-bottom .2s',
           padding: '0 24px' },
  a: { fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.6, color: LP.mutedSlate, margin: 0 },

  stillStuck: { marginTop: 40, padding: 24, background: '#fff', borderWidth: 1, borderStyle: 'solid',
                borderColor: LP.mist, borderRadius: 16, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' as const,
                fontFamily: FONT_BODY, fontSize: 15 },
  stillLink: { color: LP.iconPurple, fontWeight: 600, textDecoration: 'none' },
};

export function SupportPage() {
  return (
    <div style={{ flex: 1, background: '#fff' }}>
      <SupportHero />
      <FAQSection />
    </div>
  );
}
